import React, { useState } from "react";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Flag, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

const CATEGORIES = [
  { key: "harassment", label: "Harassment or threats" },
  { key: "fraud", label: "Suspected fraud or impersonation" },
  { key: "inappropriate", label: "Inappropriate or abusive content" },
  { key: "unknown", label: "I don't recognise this person or debt" },
  { key: "incorrect", label: "Incorrect or misleading debt information" },
  { key: "other", label: "Other" },
];

// basePath is the token-scoped public path, e.g. `/a/<token>` or `/d/<token>`.
export default function ReportSheet({ open, onOpenChange, basePath, onResolved }) {
  const [view, setView] = useState("menu"); // menu | report | block | done
  const [doneKind, setDoneKind] = useState(null); // reported | blocked
  const [category, setCategory] = useState(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => { setView("menu"); setDoneKind(null); setCategory(null); setComment(""); };
  const close = () => {
    const kind = doneKind;
    onOpenChange(false);
    setTimeout(() => { reset(); if (kind && onResolved) onResolved(kind); }, 250);
  };

  const submitReport = async () => {
    if (!category) return toast.error("Please choose a reason.");
    setSubmitting(true);
    try {
      await api.post(`${basePath}/report`, { category, comment: comment || null });
      setDoneKind("reported");
      setView("done");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const submitBlock = async () => {
    setSubmitting(true);
    try {
      await api.post(`${basePath}/block`);
      setDoneKind("blocked");
      setView("done");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DrawerContent className="max-w-[30rem] mx-auto">
        <DrawerHeader className="text-left">
          <DrawerTitle className="font-display text-xl">
            {view === "block" ? "Block this sender" : view === "done" ? (doneKind === "blocked" ? "Request blocked" : "Report received") : "Report this request"}
          </DrawerTitle>
        </DrawerHeader>
        <div className="px-5 pb-8 space-y-4 max-h-[75vh] overflow-y-auto no-scrollbar">
          {view === "menu" && (
            <div className="space-y-2.5">
              <button data-testid="report-open-report" onClick={() => setView("report")}
                className="w-full flex items-center gap-3 rounded-xl border border-border bg-white px-4 py-3.5 text-left">
                <Flag className="h-5 w-5 text-amber-600 shrink-0" />
                <span className="text-sm font-semibold text-foreground">Report this request</span>
              </button>
              <button data-testid="report-open-block" onClick={() => setView("block")}
                className="w-full flex items-center gap-3 rounded-xl border border-border bg-white px-4 py-3.5 text-left">
                <ShieldOff className="h-5 w-5 text-red-600 shrink-0" />
                <span className="text-sm font-semibold text-foreground">Block this sender</span>
              </button>
              <p className="text-xs text-muted-foreground pt-1">
                Debii never shares money. Reporting flags this request to our team for review.
              </p>
            </div>
          )}

          {view === "report" && (
            <>
              <div className="space-y-2">
                {CATEGORIES.map((c) => (
                  <button key={c.key} data-testid={`report-cat-${c.key}`} onClick={() => setCategory(c.key)}
                    className={`w-full text-left rounded-xl border px-4 py-3 text-sm font-medium transition-colors ${
                      category === c.key ? "border-primary bg-secondary text-primary" : "border-border bg-white text-foreground"
                    }`}>
                    {c.label}
                  </button>
                ))}
              </div>
              <Textarea data-testid="report-comment" value={comment} onChange={(e) => setComment(e.target.value)}
                placeholder="Add an explanation (optional)" rows={3} className="rounded-xl resize-none" />
              <Button data-testid="report-submit" onClick={submitReport} disabled={submitting}
                className="w-full h-12 rounded-xl">{submitting ? "Submitting…" : "Submit report"}</Button>
              <Button variant="ghost" onClick={() => setView("menu")} className="w-full h-10 rounded-xl">Back</Button>
            </>
          )}

          {view === "block" && (
            <>
              <p className="text-sm text-muted-foreground">
                Blocking stops this sender from re-sending or requesting acknowledgement on this specific record.
              </p>
              <div className="rounded-2xl bg-secondary p-3">
                <p className="text-xs text-muted-foreground">
                  Because you don't have a Debii account, this block applies to this request only. To block a sender
                  across everything, create a free Debii account.
                </p>
              </div>
              <Button data-testid="block-submit" onClick={submitBlock} disabled={submitting}
                className="w-full h-12 rounded-xl bg-red-600 hover:bg-red-700 text-white">
                {submitting ? "Blocking…" : "Block this sender"}
              </Button>
              <Button variant="ghost" onClick={() => setView("menu")} className="w-full h-10 rounded-xl">Back</Button>
            </>
          )}

          {view === "done" && (
            <div className="text-center py-4" data-testid={doneKind === "blocked" ? "report-done-blocked" : "report-done-reported"}>
              <p className="text-sm text-muted-foreground">
                {doneKind === "blocked"
                  ? "Request blocked. This sender can no longer ask you to acknowledge, dispute or repay this request."
                  : "Report received. Our team will review this request. You can close this page."}
              </p>
              <Button data-testid="report-done-close" onClick={close} className="mt-4 w-full h-11 rounded-xl">Close</Button>
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
